import React, { useState } from 'react'
import { type LoginPayload } from '../../../api-schema-types/user/AuthPayloadDtos'
import { AuthenticationSource } from '../../../api-services/user/AuthenticationSource'
import { Formik } from 'formik'
import { AuthValidations } from '../../../validations/AuthValidations'
import { useAuthLoginMutation } from '../../../api-services/user/authenticationApi'
import { isTwoFactorChallenge, type LoginResult } from '../../../api-schema-types/user/AuthDomainDtos'
import GlobalSnackbarNotifier from '../ui-widgets/GlobalSnackbarNotifier'
import { setNotification } from '../ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'
import { useHistory } from 'react-router-dom'
import { AuthService } from '../../../services/AuthService'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import FormikTextField from '../ui-widgets/FormikTextField'
import Grid from '@mui/material/Grid'
import { Button } from '@mui/material'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'
import ErrorComponent from '../ui-widgets/ErrorComponent'
import TwoFactorChallengeComponent from './TwoFactorChallengeComponent'

const LoginComponent: React.FC = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const goTo = (location: string): void => {
    history.push(location)
  }

  // Set once the password has been accepted. The admin portal only receives a session token after the one-time pin
  // has been verified, so until then all we hold is this challenge.
  const [challengeToken, setChallengeToken] = useState<string | null>(null)

  const [apiLogin, { isLoading }] = useAuthLoginMutation()

  const handleLogin = (payload: LoginPayload): void => {
    apiLogin(payload)
      .unwrap()
      .then(async (response: LoginResult) => {
        if (isTwoFactorChallenge(response)) {
          setChallengeToken(response.challengeToken)
          return
        }
        await AuthService.setLoginToken(response.accessToken)
        goTo('/app/dashboard')
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const defaultForm: LoginPayload = {
    context: AuthenticationSource.ADMIN_PORTAL,
    password: '',
    emailAddress: ''
  }

  if (challengeToken !== null) {
    return (
      <>
        <GlobalSnackbarNotifier/>

        <TwoFactorChallengeComponent
          challengeToken={challengeToken}
          onCancel={() => {
            setChallengeToken(null)
          }}
        />
      </>
    )
  }

  return (
    <>
      <GlobalSnackbarNotifier/>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5">Sign-In</Typography>
        <Typography sx={{ mt: 1, mb: 3 }}>Access the Sun Exchange Administration portal.</Typography>

        <Formik
          initialValues={defaultForm}
          validationSchema={AuthValidations.loginValidation}
          onSubmit={async (values, actions) => {
            handleLogin(values)
          }}
        >
          {formikProps => (
            <form
              autoComplete={'off'}
              autoCorrect={'off'}
              onSubmit={formikProps.handleSubmit}
            >
              <Grid container spacing={2}>
                {!formikProps.isValid && (
                  <Grid item md={12} sx={{ mt: 3 }}>
                    <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                  </Grid>
                )}
                <Grid item md={12}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName={'emailAddress'}
                    label={'Email Address'}
                    type={'email'}
                    locked={false}
                  />
                </Grid>
                <Grid item md={12}>
                  <FormikTextField
                    formikProps={formikProps}
                    fieldName={'password'}
                    label={'Password'}
                    type={'password'}
                    locked={false}
                  />
                </Grid>
                <Grid item md={12}>
                  <Button
                    variant={'outlined'}
                    type="submit"
                    sx={{ mr: 2 }}
                  >
                    Login
                    {isLoading && <ButtonLoadingComponent/>}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Paper>
    </>
  )
}

export default LoginComponent
